/** 数据快照（snapshots.md）：快照目录 + 复算校验 + 用于运行、每日增量 + 单日详情、告警、抽取当日增量、镜像新鲜度。
 *
 * `/data/daily` 的 `snapshot_id` 在停手日与「标的池为空」日是 null——解析器照后端形状放行，屏上分情况说明。
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { freshnessText, useDailyDetail, useMirror, useSnapshotVerify, type MirrorPayload } from "../../api/g4";
import { useAction } from "../../api/mutations";
import { useAlerts, useDailySnapshots, useRuns, useSnapshots } from "../../api/queries";
import type { DailySnapshot, SnapshotRecord } from "../../api/types";
import { Card, Kv, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { short, ts } from "../../lib/format";
import { EXPORT_SOURCES, exportBody, readExportJob, type ExportJob } from "../../lib/snapshotExport";
import { Missing, Section } from "../g4/Section";
import { RawDetails } from "../p2/Common";

const DATA_ALERT_KINDS = new Set(["daily_snapshot_halt", "corporate_action", "paper_history_coverage"]);

export function SnapshotsScreen(): React.ReactNode {
  const catalog = useSnapshots();
  const runs = useRuns();
  const daily = useDailySnapshots();
  const alerts = useAlerts();
  const mirror = useMirror();
  const [verifyId, setVerifyId] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);

  return (
    <>
      <PageHead title="数据快照" sub="GET /snapshots · /data/daily · /data/mirror" />
      <Lede>登记过哪些快照、能不能按标识复算；每个交易日的增量有没有出来、缺了哪些标的、有没有停手与待人工；上游镜像落后几天、漏了哪天。</Lede>

      <Card title="快照目录" sub="GET /snapshots · 复算走 GET /snapshots/{id}/verify" nopad>
        <Section query={catalog} what="快照目录" path="/snapshots">
          {(data) => (
            <>
              <Table
                cols={[{ label: "snapshot_id", mono: true }, { label: "来源" }, { label: "行数", num: true }, { label: "时间范围" }, { label: "内容摘要", mono: true }, { label: "文件" }, { label: "用于运行", wrap: true }, { label: "复算" }]}
                rows={data.snapshots.map((s) => ({
                  key: s.snapshot_id,
                  selected: verifyId === s.snapshot_id,
                  cells: [
                    s.snapshot_id,
                    s.source,
                    s.row_count,
                    `${ts(s.min_ts_ns)} ~ ${ts(s.max_ts_ns)}`,
                    short(s.content_sha256, 12),
                    s.files ? s.files.length : "未登记",
                    runs.data ? runs.data.filter((r) => r.snapshot_id === s.snapshot_id).map((r) => r.run_id).join("、") || "没有运行引用这份快照" : "—",
                    <button type="button" className="btn sm" disabled={!s.files} onClick={() => setVerifyId(s.snapshot_id)}>{s.files ? "复算" : "无法复算"}</button>,
                  ],
                }))}
                empty={data.rejected.length ? "目录里有读不懂的 manifest（见下）" : `还没登记过快照（目录 ${data.snapshot_dir} 为空或不存在）`}
              />
              {data.rejected.length ? (
                <div className="body">
                  <Note tone="warn">目录里有读不懂的 manifest：{data.rejected.map((r) => `${r.file}（${r.reason}：${r.detail}）`).join("；")}</Note>
                </div>
              ) : null}
              {verifyId ? <VerifyPanel id={verifyId} snapshot={data.snapshots.find((s) => s.snapshot_id === verifyId)} /> : null}
            </>
          )}
        </Section>
      </Card>

      <Card title="每日增量" sub="GET /data/daily · 点行看单日详情" nopad>
        <Section query={daily} what="每日增量" path="/data/daily">
          {(data) => (
            <Table
              cols={[{ label: "交易日" }, { label: "状态" }, { label: "snapshot_id", mono: true }, { label: "复用" }, { label: "标的池", num: true }, { label: "当日缺数据", wrap: true }, { label: "时间倒退", wrap: true }, { label: "停手" }, { label: "待人工" }]}
              rows={data.snapshots.map((row) => ({
                key: row.trade_date,
                cls: row.status === "halt" || row.halt_marker ? "warnrow" : "",
                selected: day === row.trade_date,
                onClick: () => setDay(row.trade_date),
                cells: [
                  row.trade_date,
                  dailyStatus(row),
                  row.snapshot_id ?? "—",
                  row.reused ? "是" : "否",
                  row.symbols.length,
                  row.missing_today.join("、") || "—",
                  row.regressions.join("、") || "—",
                  row.halt_marker ? <Pill tone="halt">是</Pill> : "否",
                  row.pending_manual.length ? <Pill tone="warn">{row.pending_manual.length} 条公司行动待人工</Pill> : "—",
                ],
              }))}
              empty="还没有每日增量记录（var/data/daily/ 为空）"
            />
          )}
        </Section>
      </Card>

      <SnapshotExportForm />

      {day ? <DailyDetail date={day} /> : null}

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="数据类告警" sub="GET /alerts（最近七天）" nopad>
          <Section query={alerts} what="告警" path="/alerts">
            {(rows) => (
              <Table
                cols={[{ label: "时间" }, { label: "类型" }, { label: "摘要" }, { label: "详情", wrap: true }]}
                rows={rows.filter((a) => DATA_ALERT_KINDS.has(a.kind)).map((a, i) => ({ key: `${a.ts_utc}-${i}`, cells: [a.ts_utc, a.kind, a.summary, a.detail] }))}
                empty="所选日期没有数据类告警"
              />
            )}
          </Section>
        </Card>
        <DailyRunForm />
      </div>

      <Card title="镜像新鲜度" sub="GET /data/mirror · 落后天数与漏天由服务端按交易日历判定" nopad>
        <Section query={mirror} what="镜像新鲜度" path="/data/mirror" missing="镜像分区不显示任何天数">
          {(data) => <MirrorTable data={data} />}
        </Section>
      </Card>
    </>
  );
}

function dailyStatus(row: DailySnapshot): React.ReactNode {
  if (row.symbols.length === 0 && row.note) return <Pill>{row.note}：当天无信号也无持仓，不需要数据</Pill>;
  if (row.status === "halt") return <Pill tone="halt">停手</Pill>;
  return <Pill tone={row.status === "ok" ? "ok" : "warn"}>{row.status}</Pill>;
}

function VerifyPanel({ id, snapshot }: { id: string; snapshot: SnapshotRecord | undefined }): React.ReactNode {
  const verify = useSnapshotVerify(id);
  if (snapshot && !snapshot.files) return <div className="body"><Note>该快照未登记文件清单，无法复算</Note></div>;
  return (
    <div className="body">
      <Section query={verify} what="复算结果" path={`/snapshots/${id}/verify`}>
        {(data) => (
          <>
            <Note tone={data.match === true ? "" : "bad"}>
              {data.match === null ? (data.detail ?? "无法复算") : data.match ? "复算一致：现场重算摘要与登记值相同" : "复算不一致：数据文件与登记不符"}（{data.checked_utc}）
            </Note>
            <Table
              cols={[{ label: "文件", mono: true }, { label: "登记", mono: true }, { label: "重算", mono: true }, { label: "存在" }, { label: "一致" }]}
              rows={data.files.map((f) => ({ key: f.path, cls: f.match ? "" : "bad", cells: [f.path, short(f.recorded, 12), f.recomputed ? short(f.recomputed, 12) : <Missing />, f.exists ? "是" : "否", f.match ? "是" : "否"] }))}
              empty="manifest 未登记文件"
            />
          </>
        )}
      </Section>
    </div>
  );
}

function DailyDetail({ date }: { date: string }): React.ReactNode {
  const detail = useDailyDetail(date);
  return (
    <Card title={`单日详情 · ${date}`} sub="GET /data/daily/{date}">
      <Section query={detail} what="单日详情" path={`/data/daily/${date}`}>
        {(data) => (
          <>
            <Kv pairs={[["状态", data.status], ["snapshot_id", data.snapshot_id ?? "—"], ["行数", data.rows ?? "—"], ["备注", data.note ?? "—"], ["标的池", data.symbols.join("、") || "—"]]} />
            {data.halt_marker ? <Note tone="bad">当日停手：缺数据 {data.missing_today.join("、") || "—"}；时间倒退 {data.regressions.join("、") || "—"}</Note> : null}
            <Table
              cols={[{ label: "标的" }, { label: "公司行动" }, { label: "日期" }, { label: "原文", wrap: true, mono: true }]}
              rows={data.pending_manual.map((m, i) => ({ key: `${m.symbol}-${i}`, cls: "warnrow", cells: [m.symbol, m.action_type ?? "—", m.action_date ?? "—", JSON.stringify(m.raw)] }))}
              empty="—（无待人工公司行动）"
            />
          </>
        )}
      </Section>
    </Card>
  );
}

function MirrorTable({ data }: { data: MirrorPayload }): React.ReactNode {
  if (!data.present) return <div className="empty">镜像同步还没跑过（无 {data.record_path}）</div>;
  return (
    <>
      <div className="body">
        {data.checked_utc === null && data.failed.includes("freshness") ? <Note tone="bad">新鲜度检查失败</Note> : null}
        {data.failed.length ? <Note tone="bad">本次同步失败的集合：{data.failed.join("、")}</Note> : null}
        <Kv pairs={[["检查时刻", data.checked_utc], ["同步开始", data.run_started_utc], ["回看天数", data.lookback_days], ["参照交易日", data.reference_day], ["约定周期", Object.entries(data.cadence_days).map(([k, v]) => `${k} ${v} 天`).join("；")]]} />
      </div>
      <Table
        cols={[{ label: "集合", mono: true }, { label: "来源线" }, { label: "行数", num: true }, { label: "最新数据日" }, { label: "落后交易日", num: true }, { label: "结论", wrap: true }, { label: "新入 / 删 / 拒", num: true }]}
        rows={data.collections.map((c) => {
          const f = freshnessText(c);
          return {
            key: c.name,
            cls: c.stale ? "bad" : "",
            cells: [c.name, c.source_line ?? "—", c.count ?? "—", c.latest_day ?? "—", c.lag_days ?? "—", <Pill tone={f.tone === "bad" ? "bad" : f.tone === "ok" ? "ok" : "neutral"}>{f.text}</Pill>, `${c.incoming ?? "—"} / ${c.deleted ?? "—"} / ${c.refused ?? "—"}`],
          };
        })}
        empty="记录里没有任何集合"
      />
    </>
  );
}

/** 抽取当日增量：`symbols` 按逗号拆成数组再提交（后端要列表）；dry_run 时不要求 operator / reason。 */
function DailyRunForm(): React.ReactNode {
  const action = useAction("/data/daily/run");
  const [v, setV] = useState({ trade_date: "", symbols: "", lookback_days: "30", dry_run: false, operator: "", reason: "" });
  const missing = !v.trade_date || (!v.dry_run && (!v.operator.trim() || !v.reason.trim()));
  const body = {
    trade_date: v.trade_date,
    symbols: v.symbols.split(",").map((s) => s.trim()).filter(Boolean),
    lookback_days: Number(v.lookback_days) || 30,
    dry_run: v.dry_run,
    ...(v.dry_run ? {} : { operator: v.operator.trim(), reason: v.reason.trim() }),
  };
  return (
    <Card title="抽取当日增量" sub="POST /data/daily/run · 停机时不拒（只写数据不下单）">
      <div className="fieldrow">
        <label className="f"><span>trade_date</span><input type="date" value={v.trade_date} onChange={(e) => setV({ ...v, trade_date: e.target.value })} /></label>
        <label className="f"><span>symbols（逗号分隔，可空）</span><input value={v.symbols} onChange={(e) => setV({ ...v, symbols: e.target.value })} /></label>
        <label className="f"><span>lookback_days</span><input value={v.lookback_days} onChange={(e) => setV({ ...v, lookback_days: e.target.value })} /></label>
      </div>
      <div className="fieldrow">
        <label className="f check"><span>dry_run</span><input type="checkbox" checked={v.dry_run} onChange={(e) => setV({ ...v, dry_run: e.target.checked })} /></label>
        <label className="f"><span>operator</span><input value={v.operator} onChange={(e) => setV({ ...v, operator: e.target.value })} /></label>
        <label className="f"><span>reason</span><input value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} /></label>
      </div>
      <button className="btn primary" disabled={missing || action.isPending} onClick={() => action.mutate(body)}>{action.isPending ? "提交中…" : v.dry_run ? "预演（只回标的池）" : "提交"}</button>
      {action.isError ? <Note tone="bad">{action.error instanceof Error ? action.error.message : String(action.error)}</Note> : null}
      {action.isSuccess ? <RawDetails value={action.data as object} /> : null}
    </Card>
  );
}

/** 从 JSONL 生成快照（#5011）：受理后轮询 `GET /jobs/{job_id}`，结束即刷新快照目录。 */
function SnapshotExportForm(): React.ReactNode {
  const action = useAction("/snapshots/export");
  const client = useQueryClient();
  const [v, setV] = useState({ input: "", source: "stock_data", operator: "", reason: "" });
  const built = exportBody(v);
  const jobId = action.isSuccess ? ((action.data as { job_id?: unknown })?.job_id as string | undefined) ?? null : null;
  const job = useQuery<ExportJob, Error>({
    queryKey: ["snapshot-export-job", jobId],
    queryFn: () => readExportJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 1_000 : false),
  });
  const finished = job.data && job.data.status !== "running";
  useEffect(() => {
    if (finished) void client.invalidateQueries();
  }, [finished, client]);
  return (
    <Card title="从 JSONL 生成快照" sub="POST /snapshots/export · 后台任务 · 停机时不拒（只写快照目录不下单）">
      <div className="fieldrow">
        <label className="f"><span>input（服务机上的 JSONL 路径，限 var/ 或 config/ 下；相对路径按 var/ 解释）</span><input value={v.input} onChange={(e) => setV({ ...v, input: e.target.value })} /></label>
        <label className="f">
          <span>source</span>
          <select value={v.source} onChange={(e) => setV({ ...v, source: e.target.value })}>
            {EXPORT_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="f"><span>operator</span><input value={v.operator} onChange={(e) => setV({ ...v, operator: e.target.value })} /></label>
        <label className="f"><span>reason</span><input value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} /></label>
      </div>
      <button className="btn primary" disabled={!built.ok || action.isPending} onClick={() => built.ok && action.mutate(built.body)}>{action.isPending ? "提交中…" : "生成快照"}</button>
      {!built.ok ? <Note tone="warn">{built.error}</Note> : null}
      {action.isError ? <Note tone="bad">{action.error instanceof Error ? action.error.message : String(action.error)}</Note> : null}
      {jobId ? (
        job.isError ? (
          <Note tone="bad">任务 {jobId} 查询失败：{job.error.message}</Note>
        ) : !job.data || job.data.status === "running" ? (
          <Note>任务 {jobId} 运行中…</Note>
        ) : job.data.status === "ok" && job.data.result ? (
          <Note>{job.data.result.reused ? "同内容快照已存在，复用" : "已生成"} {job.data.result.snapshot_id}，可在快照目录点「复算」核对</Note>
        ) : (
          <Note tone="bad">任务 {jobId} 失败：{job.data.error ?? "未写原因"}</Note>
        )
      ) : null}
    </Card>
  );
}
